import { TanstackError } from "../client";
import { useMutation } from "@tanstack/react-query";
import type { ApiResponseBody } from "@onsemble/shared";
import {
  LIST_PHOTO_STREAM_CONTENT_TYPE,
  type ExtractionStage,
  type ListPhotoStreamEvent,
} from "@shared/list-progress";

/**
 * JSON body for POST /api/list/photo (200), plus `clampedCount` which is only
 * carried over the streaming complete event (not part of the OpenAPI schema).
 */
export type PostPhotoMutationResult = ApiResponseBody["postPhoto"][200] & {
  clampedCount?: number;
};

/**
 * Consume an NDJSON stream, invoking `onEvent` for each parsed event. Handles
 * events split across chunk boundaries by buffering up to the last newline.
 */
async function readNdjsonStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: ListPhotoStreamEvent) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushLines = (final: boolean) => {
    const lines = buffer.split("\n");
    // Keep the trailing partial line in the buffer unless this is the final
    // flush (where any remaining content is a complete line).
    buffer = final ? "" : (lines.pop() ?? "");
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed) onEvent(JSON.parse(trimmed) as ListPhotoStreamEvent);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    flushLines(false);
  }
  buffer += decoder.decode();
  flushLines(true);
}

/**
 * Either input the endpoint accepts: a photo/PDF file, or raw list text the user
 * typed. Text is sent as JSON and joins the same extraction path server-side.
 */
export type PostPhotoInput = { onStage?: (stage: ExtractionStage) => void } & (
  | { file: File; text?: never }
  | { text: string; file?: never }
);

/**
 * Submit a list as a photo/PDF or as raw text. The extraction path streams
 * NDJSON stage events so the caller can show real progress via `onStage`;
 * pre-extraction validation errors come back as a single JSON response.
 * Resolves with the final result and throws a `TanstackError` on failure —
 * same contract the rest of the app's mutations use.
 */
export const usePostPhoto = () => {
  return useMutation({
    mutationFn: async ({
      file,
      text,
      onStage,
    }: PostPhotoInput): Promise<PostPhotoMutationResult> => {
      const requestInit: RequestInit = text !== undefined
        ? {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
            credentials: "include",
          }
        : (() => {
            const formData = new FormData();
            formData.append("photo", file as File);
            return { method: "POST", body: formData, credentials: "include" } as const;
          })();

      let response: Response;
      try {
        response = await fetch("/api/list/photo", requestInit);
      } catch {
        throw new TanstackError(0, "NETWORK_ERROR");
      }

      const contentType = response.headers.get("Content-Type") ?? "";

      // Non-streaming responses: pre-extraction validation errors (4xx).
      if (!contentType.includes(LIST_PHOTO_STREAM_CONTENT_TYPE)) {
        const data = await response.json().catch(() => undefined);
        if (!response.ok) {
          throw new TanstackError(response.status, data?.error);
        }
        return data as PostPhotoMutationResult;
      }

      // Streaming extraction path: drive progress off stage events and resolve
      // with the terminal complete/error event.
      if (!response.body) {
        throw new TanstackError(0, "UPLOAD_INTERRUPTED");
      }
      let result: PostPhotoMutationResult | undefined;
      let streamError: TanstackError | undefined;

      await readNdjsonStream(response.body, (event) => {
        switch (event.type) {
          case "stage":
            onStage?.(event.stage);
            break;
          case "complete":
            result = {
              status: "complete",
              items: event.items,
              clampedCount: event.clampedCount,
            };
            break;
          case "error":
            // Mirror the server's status classification onto the HTTP-like
            // status the app's error handling expects (unreadable/transient
            // both map to a 400-style client failure).
            streamError = new TanstackError(400, event.message);
            break;
        }
      });

      if (streamError) throw streamError;
      if (!result) {
        throw new TanstackError(0, "UPLOAD_INTERRUPTED");
      }
      return result;
    },
  });
};
